import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FacilityPicker } from "components/FacilityPicker";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import {
  WASTE_CONTAINER_TYPES,
  WASTE_CONTAINER_TYPE_BY_CODE,
  isWasteContainerTypeCode,
} from "encamp-shared/src/constants/wasteContainerTypes";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import {
  FacilityAccumulationAreaOptionsQuery,
  FacilityWasteStreamOptionsQuery,
  GetWasteContainerQuery,
  Issue,
  IssueLevel,
  UnitType,
  WasteAccumulationAreaType,
  WasteContainerCreateInput,
  WasteContainerUpdateInput,
} from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { toUtcMidnightIsoFromDateLike } from "util/dates";
import { hasCriticalIssues } from "util/forms";

import {
  AccumulationAreaDialog,
  CreatedAccumulationArea,
} from "../AccumulationAreas/AccumulationAreaDialog";
import {
  CREATE_WASTE_CONTAINER_MUTATION,
  FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY,
  FACILITY_WASTE_STREAM_OPTIONS_QUERY,
  UPDATE_WASTE_CONTAINER_MUTATION,
  VALIDATE_WASTE_CONTAINER_INPUT,
} from "./api";
import { toGraphqlUnitType, UNIT_SELECT_ITEMS } from "./containerDisplay";
import { MoveContainerDialog } from "./MoveContainerDialog";

type WasteContainerDetail = NonNullable<GetWasteContainerQuery["wasteContainer"]>;

type WasteStreamOption = NonNullable<
  NonNullable<FacilityWasteStreamOptionsQuery["facility"]>["wasteStreams"]
>["items"][number];

type AreaOption = NonNullable<
  NonNullable<FacilityAccumulationAreaOptionsQuery["facility"]>["accumulationAreas"]
>["items"][number];

/**
 * The area picker mixes the real areas with a synthetic "create a new area"
 * entry. A discriminated union keeps the create entry out of the AreaOption
 * space entirely, so it can never collide with a real area id or leak into
 * the form value.
 */
type AreaPickerOption = { kind: "area"; area: AreaOption } | { kind: "create" };

function areaTypeLabel(type: WasteAccumulationAreaType): string {
  return type === WasteAccumulationAreaType.Central ? "Central" : "Satellite";
}

type WasteContainerFormValues = {
  facilityId: string;
  containerLabel: string;
  containerType: string;
  capacity: string;
  capacityUnit: UnitType | null;
  isPermanent: boolean;
  isFlowThrough: boolean;
  wasteStreamId: string | null;
  accumulationAreaId: string | null;
  initialAmount: string;
  initialAmountUnit: UnitType | null;
  isEstimate: boolean;
  accumulationStartDate: string | null;
  movedToCentralDate: string | null;
  sqgOver200Miles: boolean;
  estimatedPickupDate: string | null;
};

const CONTAINER_TYPE_SELECT_ITEMS = WASTE_CONTAINER_TYPES.map((type) => ({
  value: type.code,
  display: `${type.label} (${type.code})`,
}));

function toDateTimeInput(isoDate: string | null): string | undefined {
  if (!isoDate) {
    return undefined;
  }
  return toUtcMidnightIsoFromDateLike(DateTime.fromISO(isoDate));
}

/**
 * Fields whose form→input mapping is byte-for-byte the same for create and
 * update. Both input mappers spread this so a new shared column is wired in
 * one place instead of drifting between the two. containerLabel is not here:
 * its blank handling diverges by mode (create auto-generates a label, edit
 * rejects a blank), like the nullable fields in toContainerUpdateInput.
 */
function toSharedContainerFields(values: WasteContainerFormValues) {
  return {
    containerType: values.containerType,
    isPermanent: values.isPermanent,
    isFlowThrough: values.isFlowThrough,
    isEstimate: values.isEstimate,
    sqgOver200Miles: values.sqgOver200Miles,
  };
}

/**
 * The validator takes the create-input shape for both modes; edit maps its
 * identity fields onto it, plus the container's CURRENT area so placement
 * rules (tanks are central-only) block inline instead of surfacing as a raw
 * mutation error after Save.
 */
function toContainerInput(
  values: WasteContainerFormValues,
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null
): WasteContainerCreateInput {
  return {
    ...toSharedContainerFields(values),
    // Create treats a blank label as "auto-generate"; edit sends the trimmed
    // value verbatim so a cleared field reaches the validator/mutation as ""
    // and is rejected rather than silently reverting to the stored label.
    containerLabel:
      mode === "create"
        ? (values.containerLabel ?? "").trim() || undefined
        : (values.containerLabel ?? "").trim(),
    facilityId: values.facilityId,
    capacity: safeParseFloat(values.capacity) ?? undefined,
    capacityUnit: values.capacityUnit ?? undefined,
    wasteStreamId: values.wasteStreamId ?? undefined,
    estimatedPickupDate: toDateTimeInput(values.estimatedPickupDate),
    ...(mode === "create"
      ? {
          accumulationAreaId: values.accumulationAreaId ?? undefined,
          initialAmount: safeParseFloat(values.initialAmount) ?? undefined,
          initialAmountUnit: values.initialAmountUnit ?? undefined,
          accumulationStartDate: toDateTimeInput(values.accumulationStartDate),
          movedToCentralDate: toDateTimeInput(values.movedToCentralDate),
        }
      : { accumulationAreaId: currentAccumulationAreaId ?? undefined }),
  };
}

/**
 * Update mutation input. Shares the common scalars with create via
 * toSharedContainerFields; the nullable fields diverge deliberately — here a
 * null is an instruction to clear (detach the stream, drop the capacity/unit),
 * whereas create coalesces the same fields to undefined ("leave unset").
 */
function toContainerUpdateInput(
  values: WasteContainerFormValues,
  containerId: string,
  estimatedPickupDateDirty: boolean
): WasteContainerUpdateInput {
  return {
    ...toSharedContainerFields(values),
    // A cleared label arrives as "" so the mutation's blank guard rejects it
    // instead of Prisma leaving the stored label unchanged.
    containerLabel: (values.containerLabel ?? "").trim(),
    id: containerId,
    capacity: safeParseFloat(values.capacity),
    capacityUnit: values.capacityUnit,
    wasteStreamId: values.wasteStreamId,
    // Only send the date when the user actually touched it: re-normalizing the
    // stored instant through the browser's local zone can shift it to a
    // different calendar day for negative-UTC-offset users.
    estimatedPickupDate: estimatedPickupDateDirty
      ? (toDateTimeInput(values.estimatedPickupDate) ?? null)
      : undefined,
  };
}

function missingFacilityIssue(): Issue {
  return {
    level: IssueLevel.Critical,
    key: "facilityId",
    message: "Select a facility for this container",
    modelName: "WasteContainer",
    dataName: null,
    jurisdictions: [],
  };
}

function useWasteContainerInputValidation(
  mode: "create" | "edit",
  currentAccumulationAreaId?: string | null,
  currentContainerId?: string | null
) {
  const [query] = useLazyQuery(VALIDATE_WASTE_CONTAINER_INPUT, {
    fetchPolicy: "no-cache",
  });

  return useCallback(
    async (values: WasteContainerFormValues): Promise<Issue[]> => {
      if (!values.facilityId) {
        return [missingFacilityIssue()];
      }
      const { data } = await query({
        variables: {
          input: toContainerInput(values, mode, currentAccumulationAreaId),
          // Editing: the container's own label must not read as a collision.
          currentContainerId: currentContainerId ?? undefined,
        },
      });
      return data?.wasteContainerInputValidator ?? [];
    },
    [query, mode, currentAccumulationAreaId, currentContainerId]
  );
}

export type WasteContainerDialogProps = {
  onClose: () => void;
  /** Locks the create form to this facility (facility-scoped list, tile). */
  facilityId?: string;
  /** Present in edit mode. */
  container?: WasteContainerDetail;
  onCreated?: (containerId: string) => void;
};

export function WasteContainerDialog({
  onClose,
  facilityId,
  container,
  onCreated,
}: WasteContainerDialogProps) {
  const alerts = useAlerts();
  const mode: "create" | "edit" = container ? "edit" : "create";

  const [selectedWasteStream, setSelectedWasteStream] = useState<WasteStreamOption | null>(
    container?.wasteStream ?? null
  );
  // Create-only: the picked area (id lives in form state; the option object
  // is kept alongside so the Autocomplete can render its label/type without
  // a second lookup, mirroring selectedWasteStream above).
  const [selectedArea, setSelectedArea] = useState<AreaOption | null>(null);
  const [newAreaDialogOpen, setNewAreaDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  // The live validator flags the empty container type the moment a facility is
  // picked; suppress that field-level error text until the field is touched or
  // a save is attempted, so an untouched form doesn't open pre-shouting. The
  // issue counter and disabled Save still reflect the issue immediately.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const defaultValues = useMemo<WasteContainerFormValues>(
    () => ({
      facilityId: container?.facilityId ?? facilityId ?? "",
      containerLabel: container?.containerLabel ?? "",
      containerType: container?.containerType ?? "",
      capacity: container?.capacity != null ? String(container.capacity) : "",
      capacityUnit: container?.capacityUnit ?? null,
      isPermanent: container?.isPermanent ?? false,
      isFlowThrough: container?.isFlowThrough ?? false,
      wasteStreamId: container?.wasteStream?.id ?? null,
      accumulationAreaId: null,
      initialAmount: "",
      initialAmountUnit: null,
      isEstimate: container?.isEstimate ?? false,
      accumulationStartDate: null,
      movedToCentralDate: null,
      sqgOver200Miles: container?.sqgOver200Miles ?? false,
      estimatedPickupDate: container?.estimatedPickupDate ?? null,
    }),
    [container, facilityId]
  );

  const validateInput = useWasteContainerInputValidation(
    mode,
    container?.accumulationArea?.id ?? null,
    container?.id ?? null
  );
  const form = useValidatingForm<WasteContainerFormValues>(defaultValues, [], validateInput);
  const {
    control,
    getValues,
    setValue,
    watch,
    issues,
    formState: { isDirty, dirtyFields },
  } = form;

  const watchedFacilityId = watch("facilityId");
  const watchedIsPermanent = watch("isPermanent");

  // Placement is create-only, and a tank must sit in a central area. A
  // container already in a satellite area can never be made permanent from
  // this form, so lock the checkbox instead of letting the user walk into an
  // unresolvable critical placement issue that leaves Save disabled.
  const tankPlacementLocked =
    mode === "edit" &&
    container?.accumulationArea != null &&
    container.accumulationArea.type !== WasteAccumulationAreaType.Central;

  const { data: areaOptionsData } = useQuery(FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY, {
    variables: { facilityId: watchedFacilityId },
    skip: mode !== "create" || !watchedFacilityId,
  });
  const areaOptions: AreaOption[] = areaOptionsData?.facility?.accumulationAreas?.items ?? [];
  const filteredAreaOptions = watchedIsPermanent
    ? areaOptions.filter((area) => area.type === WasteAccumulationAreaType.Central)
    : areaOptions;
  // An inline-created area is selected before the options query refetches,
  // so it may not be in the list above yet — include it directly, or MUI
  // logs a "value not in options" warning and the field appears to clear.
  const visibleAreaOptions =
    selectedArea && !filteredAreaOptions.some((area) => area.id === selectedArea.id)
      ? [...filteredAreaOptions, selectedArea]
      : filteredAreaOptions;

  const handleAreaCreated = useCallback(
    (area: CreatedAccumulationArea) => {
      setSelectedArea(area);
      setValue("accumulationAreaId", area.id, { shouldDirty: true, shouldValidate: true });
      setNewAreaDialogOpen(false);
    },
    [setValue]
  );

  // Selecting a container type prefills capacity from the industry-standard
  // size when the user hasn't entered one.
  const prefillCapacityForType = useCallback(
    (typeCode: string) => {
      if (!isWasteContainerTypeCode(typeCode)) {
        return;
      }
      const definition = WASTE_CONTAINER_TYPE_BY_CODE[typeCode];
      if (definition.defaultCapacity == null || definition.defaultCapacityUnit == null) {
        return;
      }
      if ((getValues("capacity") ?? "").trim() !== "") {
        return;
      }
      setValue("capacity", String(definition.defaultCapacity), { shouldDirty: true });
      setValue("capacityUnit", toGraphqlUnitType(definition.defaultCapacityUnit), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [getValues, setValue]
  );

  const [createContainer, { loading: isCreating }] = useMutation(CREATE_WASTE_CONTAINER_MUTATION, {
    refetchQueries: [
      "TenantWasteContainers",
      "FacilityWasteContainers",
      "FacilityWasteContainersCount",
    ],
  });
  const [updateContainer, { loading: isUpdating }] = useMutation(UPDATE_WASTE_CONTAINER_MUTATION, {
    refetchQueries: ["TenantWasteContainers", "FacilityWasteContainers", "GetWasteContainer"],
  });

  const onSubmit = async () => {
    setSubmitAttempted(true);
    const values = getValues();
    // Warn-and-allow gate: CRITICAL issues block the save; ERROR issues have
    // already been surfaced on the fields and in the issue list but the user
    // may still save through them.
    const submitIssues = await validateInput(values);
    if (hasCriticalIssues(submitIssues)) {
      const firstCritical = submitIssues.find((issue) => issue.level === IssueLevel.Critical);
      alerts.error(firstCritical?.message ?? "Fix the critical issues before saving");
      return;
    }
    if (mode === "create") {
      await createContainer({
        variables: { input: toContainerInput(values, "create") },
        onCompleted: (data) => {
          alerts.success("Container created");
          onCreated?.(data.createWasteContainer.id);
          onClose();
        },
        onError: (error) => {
          alerts.error(error.message ?? "An error occurred while creating the container");
        },
      });
      return;
    }
    if (!container) {
      return;
    }
    const input = toContainerUpdateInput(
      values,
      container.id,
      Boolean(dirtyFields.estimatedPickupDate)
    );
    await updateContainer({
      variables: { input },
      onCompleted: () => {
        alerts.success("Container updated");
        onClose();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while updating the container");
      },
    });
  };

  const isSaving = isCreating || isUpdating;

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        maxWidth="sm"
        title={mode === "create" ? "Add container" : "Edit container"}
        primaryActionButtonProps={{
          loading: isSaving,
          onClick: onSubmit,
          disabled: hasCriticalIssues(issues) || !isDirty,
        }}
        additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      >
        <FormProvider {...form}>
          <Box component="form" sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              {mode === "create" && !facilityId && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="facilityId"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FacilityPicker
                        label="Facility"
                        facilityQueryVariant="waste"
                        textFieldProps={{ required: true }}
                        value={field.value || null}
                        onChange={(facility) => {
                          const nextFacilityId = facility?.id ?? "";
                          // A waste stream belongs to a facility; carrying the
                          // old selection across a facility switch dead-ends
                          // the save on "belongs to a different facility".
                          if (nextFacilityId !== field.value) {
                            setValue("wasteStreamId", null, { shouldValidate: true });
                            setSelectedWasteStream(null);
                            setValue("accumulationAreaId", null, { shouldValidate: true });
                            setSelectedArea(null);
                          }
                          field.onChange(nextFacilityId);
                        }}
                        {...fieldState}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <FormTextField
                  name="containerLabel"
                  label="Label"
                  control={control}
                  helperText={
                    mode === "create" ? "Leave blank to auto-generate a label" : undefined
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="containerType"
                  control={control}
                  render={({ field, fieldState }) => {
                    const showError = submitAttempted || fieldState.isTouched || fieldState.isDirty;
                    return (
                      <TextField
                        select
                        fullWidth
                        required
                        label="Container type"
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                          prefillCapacityForType(event.target.value);
                        }}
                        error={showError && !!fieldState.error}
                        helperText={showError ? fieldState.error?.message : undefined}
                      >
                        {CONTAINER_TYPE_SELECT_ITEMS.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.display}
                          </MenuItem>
                        ))}
                      </TextField>
                    );
                  }}
                />
              </Grid>
              <Grid size={{ xs: 7 }}>
                <FormTextField name="capacity" label="Capacity" control={control} type="number" />
              </Grid>
              <Grid size={{ xs: 5 }}>
                <FormSelect
                  name="capacityUnit"
                  label="Capacity unit"
                  control={control}
                  selectItems={UNIT_SELECT_ITEMS}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <AsyncAutocomplete
                  query={FACILITY_WASTE_STREAM_OPTIONS_QUERY}
                  variables={{ facilityId: watchedFacilityId }}
                  getItems={(data: FacilityWasteStreamOptionsQuery) =>
                    data.facility?.wasteStreams?.items ?? []
                  }
                  getOptionLabel={(stream: WasteStreamOption) => stream.name}
                  value={selectedWasteStream}
                  onChange={(stream) => {
                    setSelectedWasteStream(stream);
                    setValue("wasteStreamId", stream?.id ?? null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  disabled={!watchedFacilityId}
                  lazyLoad
                  fullWidth
                  // AsyncAutocomplete labels the field via placeholderLabel;
                  // textFieldProps.label is overridden by it, so set this.
                  placeholderLabel="Waste stream"
                  initialSortModel={[{ field: "name", sort: "asc" }]}
                />
              </Grid>
              {mode === "create" && (
                <Grid size={{ xs: 12 }}>
                  <Autocomplete<AreaPickerOption>
                    options={[
                      ...visibleAreaOptions.map(
                        (area): AreaPickerOption => ({ kind: "area", area })
                      ),
                      { kind: "create" },
                    ]}
                    getOptionLabel={(option) =>
                      option.kind === "create" ? "+ New area" : option.area.name
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.kind === "create"
                        ? value.kind === "create"
                        : value.kind === "area" && option.area.id === value.area.id
                    }
                    value={selectedArea ? { kind: "area", area: selectedArea } : null}
                    disabled={!watchedFacilityId}
                    filterOptions={(options, state) => {
                      const query = state.inputValue.trim().toLowerCase();
                      return options.filter(
                        (option) =>
                          option.kind === "create" || option.area.name.toLowerCase().includes(query)
                      );
                    }}
                    onChange={(_event, value) => {
                      if (value?.kind === "create") {
                        setNewAreaDialogOpen(true);
                        return;
                      }
                      setSelectedArea(value?.area ?? null);
                      setValue("accumulationAreaId", value?.area.id ?? null, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    renderOption={(props, option) =>
                      option.kind === "create" ? (
                        <li {...props} key="create">
                          <Add fontSize="small" sx={{ mr: 1 }} />
                          New area
                        </li>
                      ) : (
                        <li {...props} key={option.area.id}>
                          {option.area.name} ({areaTypeLabel(option.area.type)})
                        </li>
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Accumulation area"
                        helperText={watchedFacilityId ? undefined : "Select a facility first"}
                      />
                    )}
                  />
                </Grid>
              )}
              {mode === "edit" && container && (
                <Grid size={{ xs: 12 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Accumulation area"
                      fullWidth
                      disabled
                      value={container.accumulationArea?.name ?? "Not yet placed"}
                    />
                    <Button
                      size="small"
                      sx={{ mt: 1, whiteSpace: "nowrap" }}
                      onClick={() => setMoveDialogOpen(true)}
                    >
                      Move…
                    </Button>
                  </Stack>
                </Grid>
              )}
              {mode === "create" && (
                <>
                  <Grid size={{ xs: 7 }}>
                    <FormTextField
                      name="initialAmount"
                      label="Initial amount"
                      control={control}
                      type="number"
                      helperText="Recorded as the container's first fill event"
                    />
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    <FormSelect
                      name="initialAmountUnit"
                      label="Amount unit"
                      control={control}
                      selectItems={UNIT_SELECT_ITEMS}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DateField
                      name="accumulationStartDate"
                      label="Accumulation start date"
                      control={control}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <DateField
                      name="movedToCentralDate"
                      label="Moved to central date"
                      control={control}
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: mode === "create" ? 6 : 12 }}>
                <DateField
                  name="estimatedPickupDate"
                  label="Estimated pickup date"
                  control={control}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CheckboxField
                  name="isEstimate"
                  label="Amounts are estimates"
                  control={control}
                  noErrorDisplay
                />
                <CheckboxField
                  name="isPermanent"
                  label="Permanent vessel (tank)"
                  control={control}
                  disabled={tankPlacementLocked}
                  tooltip={
                    tankPlacementLocked
                      ? "Tanks can only be placed in central accumulation areas. This container is in a satellite area, so it can't be made permanent here."
                      : undefined
                  }
                  checkboxProps={{
                    onChange: (_event, checked) => {
                      // Flow-through requires permanent; clear it when permanent
                      // is unchecked, or the disabled flow-through checkbox holds
                      // a stale true the user can no longer clear.
                      if (!checked) {
                        setValue("isFlowThrough", false, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                      // A tank can only sit in a central area — clear a
                      // now-invalid satellite pick instead of leaving a stale
                      // selection that would only surface as a critical issue.
                      if (
                        checked &&
                        selectedArea &&
                        selectedArea.type !== WasteAccumulationAreaType.Central
                      ) {
                        setSelectedArea(null);
                        setValue("accumulationAreaId", null, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }
                    },
                  }}
                  noErrorDisplay
                />
                <CheckboxField
                  name="isFlowThrough"
                  label="Flow-through (clock resets when emptied)"
                  control={control}
                  disabled={!watchedIsPermanent}
                  noErrorDisplay
                />
                <CheckboxField
                  name="sqgOver200Miles"
                  label="SQG shipping 200+ miles to the designated facility"
                  control={control}
                  noErrorDisplay
                />
              </Grid>
              {mode === "edit" && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" variant="outlined">
                    <Typography variant="body2">
                      Amounts and status change through fill events, not from this form.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </FormProvider>
      </Dialog>
      {mode === "create" && newAreaDialogOpen && watchedFacilityId && (
        <AccumulationAreaDialog
          open
          facilityId={watchedFacilityId}
          onClose={() => setNewAreaDialogOpen(false)}
          onCreated={handleAreaCreated}
        />
      )}
      {mode === "edit" && container && (
        <MoveContainerDialog
          container={container}
          open={moveDialogOpen}
          onClose={() => setMoveDialogOpen(false)}
        />
      )}
    </>
  );
}
